import type { ComponentType } from 'react';
import { Actividad } from './actividad/Actividad';
import { Agenda } from './agenda/Agenda';
import { Asistente } from './asistente/Asistente';
import { Avisos } from './avisos/Avisos';
import { Canales } from './canales/Canales';
import { Buzon } from './buzon/Buzon';
import { Campanas } from './campanas/Campanas';
import { Clientes } from './clientes/Clientes';
import { Cuenta } from './cuenta/Cuenta';
import { AceptarPropiedad } from './equipo/AceptarPropiedad';
import { Equipo } from './equipo/Equipo';
import { Inicio } from './inicio/Inicio';
import { Indicadores } from './indicadores/Indicadores';
import { Eventos } from './eventos/Eventos';
import { Listas } from './listas/Listas';
import { Mensajes } from './mensajes/Mensajes';
import { Negocio } from './negocio/Negocio';
import { Cobrar } from './pagos/cobrar/Cobrar';
import { Pagos } from './pagos/Pagos';
import { Pedidos } from './pedidos/Pedidos';
import { Plan } from './plan/Plan';
import { Plano } from './plano/Plano';
import { Puerta } from './puerta/Puerta';
import { Puesta } from './puesta/Puesta';
import { Recursos } from './recursos/Recursos';
import { PantallaEstancias } from './reservas/estancias/PantallaEstancias';
import { Reservas } from './reservas/Reservas';
import { Taquilla } from './taquilla/Taquilla';
import { Servicios } from './servicios/Servicios';
import { Sala } from './sala/Sala';
import { Web } from './web/Web';
import type { PropsPantalla } from './tipos';

/**
 * Pantallas del panel por la clave de su entrada del menú (`key` de getMyNavigation). El menú sigue siendo del servidor:
 * una entrada que no llega no se ve, aunque su pantalla esté aquí; y una entrada sin pantalla todavía enseña su hueco.
 */
export const PANTALLAS: Readonly<Partial<Record<string, ComponentType<PropsPantalla>>>> = {
    // Inicio (7.1, Analítica): también Recepción con «Hoy» (G774) y los botones de G800.
    inicio: Inicio,
    cuenta: Cuenta,
    equipo: Equipo,
    negocio: Negocio,
    plan: Plan,
    // La puesta en marcha (1.7): `puesta` es la clave de su entrada del menú (Negocios y equipos, G175, G341).
    puesta: Puesta,
    actividad: Actividad,
    clientes: Clientes,
    // Pagos (4.1 a 4.8) con sus apartados y «Cobrar» (4.7), la entrada aparte de Operativa (G276).
    pagos: Pagos,
    cobrar_local: Cobrar,
    // Entregas y Notificaciones: «Avisos» (5.1, 5.2) y «Mensajes» (5.3 a 5.5), con sus apartados.
    avisos: Avisos,
    mensajes: Mensajes,
    // Web pública (13.1 a 13.5): Tu web, Widgets y Medición, según los permisos de cada uno (G628).
    web: Web,
    // Asistente IA (11.5 a 11.7): Configuración, Preguntas, Instrucciones, Sin asistente y Probar y publicar en su barra.
    asistente: Asistente,
    // Canales (11.1, 11.2 y 11.4): Canales · Plantillas de Meta, según los permisos de cada uno.
    canales: Canales,
    // Buzón (11.3) con su apartado Llamadas (11.9, próximamente).
    buzon: Buzon,
    // Campañas (12.1 a 12.7): Campañas · Mensajes automáticos · Plantillas y Ajustes; el Socio, solo el resumen (G776).
    campanas: Campanas,
    // Indicadores (7.2) con su apartado Exportaciones (7.3, `indicadores_exportaciones`).
    indicadores: Indicadores,
    // Pases y Acceso: «Puerta» (6.3 Preparar, 6.4 Conflictos, 6.5 Registro; G778, G782), con su barra según los permisos.
    puerta: Puerta,
    // Reservas (8.1 Reservas del día y 8.4 Por completar), con su barra de apartados propia.
    reservas: Reservas,
    // Estancias (8.9, bloque Estancias): entrada propia del menú (G878), ya no apartado de Reservas.
    estancias: PantallaEstancias,
    // Sala en vivo (8.7, bloque Plano y sala; tablet principal), también `?imprimir=1&dia=` desde 8.1 (G796).
    sala: Sala,
    // Recursos (8.5 Zonas y recursos; Cierres 8.8 y Reservados para eventos 8.10 en sus apartados).
    recursos: Recursos,
    // Plano (8.6, bloque Plano y sala): Dibujo · Versiones, borrador único con fecha de efecto (G401, G402).
    plano: Plano,
    // Citas: «Agenda» (9.1 a 9.3) y, para el Profesional, «Mi agenda» (9.8); ruta `/citas` (G779).
    agenda: Agenda,
    // Eventos y Entradas (10.1 a 10.8): «Eventos» con la clave `entradas`, «Entradas» (Pedidos · Informes) y «Taquilla» (G778).
    entradas: Eventos,
    pedidos: Pedidos,
    taquilla: Taquilla,
    // «Listas y RRPP» (10.9 a 10.11) con sus apartados; el RRPP («lo suyo») ve su portal (10.12).
    listas: Listas,
    // Citas: «Servicios y profesionales» (9.4 Servicios con su ficha; 9.5 a 9.7 y 9.11, su hueco por ahora), ruta `/servicios` (G779).
    servicios: Servicios,
};

/**
 * Pantallas que pintan ellas mismas sus apartados (`nested_as: section`) en su barra (G141): el marco no los repite debajo
 * como tarjetas. Las tarjetas (`card`) sí las sigue pintando el marco.
 */
export const PANTALLAS_CON_APARTADOS: ReadonlySet<string> = new Set(['clientes', 'negocio', 'pagos', 'avisos', 'mensajes', 'indicadores', 'pedidos', 'listas']);

/**
 * Tarjetas (`nested_as: card`) que pinta la propia pantalla entre las suyas, por la clave de la pantalla madre: el marco
 * no las repite debajo. «Negocio» pinta «Cuándo se puede reservar» (`reglas_reserva`, G244) como una de sus tarjetas de
 * ajustes, y «Políticas de pago» (`politicas_pago`, G130); las demás (Web) las sigue pintando el marco hasta que lleguen con su módulo.
 */
export const TARJETAS_PROPIAS: Readonly<Partial<Record<string, ReadonlySet<string>>>> = {
    negocio: new Set(['reglas_reserva', 'politicas_pago']),
};

/**
 * Pantallas que no son una entrada del menú, por su ruta: a ellas se llega desde un enlace (un correo, la campana u otra
 * pantalla). La API decide quién puede usarlas.
 */
export const PANTALLAS_SUELTAS: Readonly<Partial<Record<string, { titulo: string; Componente: ComponentType<PropsPantalla> }>>> = {
    /** 1.12: aceptar o rechazar la propiedad (quien la recibe), desde la campana, el correo o Equipo. */
    '/propiedad': { titulo: 'Propiedad del negocio', Componente: AceptarPropiedad },
};

export const RUTAS_SUELTAS: readonly string[] = Object.keys(PANTALLAS_SUELTAS);
